/**
 * WorkflowStatusBar.tsx - the Workflows tab's bottom toggle rail.
 *
 * The View tab's StatusBar carries the GRAPH canvas toggles (tables /
 * projections / arrows / groups) - none of which exist on the workflow
 * canvas. This is the workflows-specific bar: Grid + Minimap toggles for the
 * canvas, plus the run + worker status dots on the right (the same rail
 * chrome, `.status-bar`).
 */
interface WorkflowStatusBarProps {
  showGrid: boolean;
  onToggleGrid: () => void;
  showMinimap: boolean;
  onToggleMinimap: () => void;
  /** The followed run's platform status (null = no run followed). */
  runStatus: string | null;
  /** Whether the per-project workflow worker subprocess is up. */
  workerRunning: boolean;
}

interface ToggleProps {
  label: string;
  pressed: boolean;
  onClick: () => void;
  icon: JSX.Element;
}

function Toggle({ label, pressed, onClick, icon }: ToggleProps): JSX.Element {
  return (
    <button
      type="button"
      className={`status-toggle${pressed ? ' pressed' : ''}`}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={pressed}
    >
      {icon}
    </button>
  );
}

export function WorkflowStatusBar(props: WorkflowStatusBarProps): JSX.Element {
  return (
    <footer className="status-bar">
      <div className="status-toggles" />
      <div className="status-center" style={{ gap: 2, display: 'flex' }}>
        <Toggle
          label="Grid"
          pressed={props.showGrid}
          onClick={props.onToggleGrid}
          icon={<GridIcon />}
        />
        <Toggle
          label="Minimap"
          pressed={props.showMinimap}
          onClick={props.onToggleMinimap}
          icon={<MinimapIcon />}
        />
      </div>
      <div className="status-right">
        {props.workerRunning && (
          <span className="wf-status-dot on" title="Workflow worker running" aria-label="Worker running">
            ●
          </span>
        )}
        {props.runStatus && (
          <span className={`wf-status-dot run-${props.runStatus}`} title={`Run: ${props.runStatus}`} aria-label={`Run ${props.runStatus}`}>
            ●
          </span>
        )}
      </div>
    </footer>
  );
}

// ─── Inline icons (16px, currentColor) - same hand-rolled set as StatusBar ──

function GridIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <line x1="2" y1="5.5" x2="14" y2="5.5" />
      <line x1="2" y1="10.5" x2="14" y2="10.5" />
      <line x1="5.5" y1="2" x2="5.5" y2="14" />
      <line x1="10.5" y1="2" x2="10.5" y2="14" />
    </svg>
  );
}

function MinimapIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
      <rect x="4.5" y="5" width="4" height="3" fill="currentColor" stroke="none" />
      <rect x="9" y="8.5" width="3" height="3" fill="currentColor" stroke="none" opacity="0.6" />
    </svg>
  );
}
